/* li-m-evap.js — MOTION-LAB 15.10.2026 · "Verdunstung, sichtbar gemacht" (section li-m-evap).
   WebGL2, no libraries. One baked weave texture (white crepe bandage, plain weave, crimp + twist + fuzz), a perspective macro
   camera over the fabric plane with depth-of-field via mip bias, wetness + temperature fields computed per pixel, isotherm
   "instrument" view revealed by a scan line / a loupe / a toggle, and evaporating molecules as a GL_POINTS pass.
   Scroll drives the story (pinned track): soak 0–.30 · evaporate .30–.62 · scan .62–.86 · hold. Reduced motion, no WebGL or
   no JS = the static poster + all text (CSS default). Inits lazily (IntersectionObserver), renders only while on screen. */
(() => {
  const roots = document.querySelectorAll('[data-m-ev]');
  if (!roots.length) return;
  const RM = matchMedia('(prefers-reduced-motion: reduce)');
  const clamp = (x, a = 0, b = 1) => Math.min(b, Math.max(a, x));
  const ease = t => 1 - Math.pow(1 - clamp(t), 3);
  const seg = (p, a, b) => clamp((p - a) / (b - a));
  const eio = t => { t = clamp(t); return t < .5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; };

  const VS = `#version 300 es
in vec2 a; out vec2 v; void main(){ v = a * .5 + .5; gl_Position = vec4(a, 0., 1.); }`;

  /* ---- bake: periodic plain-weave height field (R height, G yarn twist detail, B gap mask) ---- */
  const BAKE = `#version 300 es
precision highp float; in vec2 v; out vec4 o;
const float N = 20.; const float TAU = 6.2831853;
float h1(float n){ return fract(sin(n * 127.1) * 43758.5453); }
float h2(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float pn(vec2 p, float per){ vec2 i = floor(p), f = fract(p); vec2 u = f * f * (3. - 2. * f);
  float a = h2(mod(i, per)), b = h2(mod(i + vec2(1, 0), per)), c = h2(mod(i + vec2(0, 1), per)), d = h2(mod(i + vec2(1, 1), per));
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y); }
void main(){
  vec2 g = v * N;
  float crk = pn(v * 4., 4.) * .6 + pn(v * 8., 8.) * .4;               // crepe crinkle (periodic)
  // crimp: thread centre lines wander (integer frequencies keep the tile seamless)
  float i = floor(g.x), j = floor(g.y);
  float ox = .13 * sin(TAU * (v.y * 2. + h1(i))) + .07 * sin(TAU * (v.y * 5. + h1(i + 7.)));
  float oy = .13 * sin(TAU * (v.x * 2. + h1(j + 3.))) + .07 * sin(TAU * (v.x * 5. + h1(j + 11.)));
  float fx = fract(g.x + ox) - .5, fy = fract(g.y + oy) - .5;
  float wi = floor(g.x + ox), wj = floor(g.y + oy);
  float ww = (.31 + .05 * h1(wi + 31.)) * (.88 + .24 * pn(vec2(wi * 3.1, v.y * 12.), 12.)), wf = (.31 + .05 * h1(wj + 57.)) * (.88 + .24 * pn(vec2(v.x * 12., wj * 3.7), 12.));  // yarn half widths (open crepe weave)
  float aw = clamp(1. - (fx * fx) / (ww * ww), 0., 1.), af = clamp(1. - (fy * fy) / (wf * wf), 0., 1.);
  float hw = sqrt(aw), hf = sqrt(af);
  float sw = cos(3.14159265 * (g.y + oy - .5 + wi)), sf = -cos(3.14159265 * (g.x + ox - .5 + wj));
  float zw = hw * (.62 + .38 * sw), zf = hf * (.62 + .38 * sf);
  // yarn twist striations + fibre fuzz
  float tw = sin(TAU * (g.y * 3. + fx * 2.2 + h1(wi))) * .5 + .5;
  float tf = sin(TAU * (g.x * 3. + fy * 2.2 + h1(wj + 5.))) * .5 + .5;
  float fz = pn(v * 160., 160.) * .55 + pn(v * 320., 320.) * .45;
  float top = max(zw, zf);
  float tws = zw > zf ? tw : tf;
  float hgt = top * (.84 + .12 * tws) + .06 * fz * step(.02, top) + .22 * crk;
  float gap = 1. - smoothstep(.0, .12, top);
  o = vec4(clamp(hgt / 1.2, 0., 1.), tws, gap, 1.);
}`;

  /* ---- frame: perspective macro over the fabric plane ---- */
  const FS = `#version 300 es
precision highp float; in vec2 v; out vec4 o;
uniform sampler2D W; uniform vec2 R; uniform vec3 C, T; uniform float F, S, TX;
uniform float uSoak, uEvap, uScan, uMode, uTime, uDry;
uniform vec4 uLoupe;   // x, y (px, GL coords), radius px, on 0..1
uniform vec2 uC;        // wet centre on the plane
uniform sampler2D IR; uniform float uIRA, uIRZ, uIRon, uRev, uHide; uniform vec2 uIRD, uWS, uIRC;
const vec3 BG = vec3(.855, .871, .871);   // #EEF0F0 in linear
vec3 lin(vec3 c){ return pow(c, vec3(2.2)); }
float h2(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float vn(vec2 p){ vec2 i = floor(p), f = fract(p); vec2 u = f * f * (3. - 2. * f);
  return mix(mix(h2(i), h2(i + vec2(1, 0)), u.x), mix(h2(i + vec2(0, 1)), h2(i + vec2(1, 1)), u.x), u.y); }
float fbm(vec2 p){ return vn(p) * .55 + vn(p * 2.03 + 7.1) * .3 + vn(p * 4.1 + 3.3) * .15; }
// wetness: anisotropic wicking front with fingers along the threads, receding patchily while it dries
float wet(vec2 p, out float edge, out float sd){
  vec2 q = p - uC;
  float d = mix(length(q * vec2(1., 1.15)), max(abs(q.x), abs(q.y) * 1.1) * 1.1, .14);
  float fing = .5 * (vn(vec2(q.x * 30., q.y * 4.)) + vn(vec2(q.x * 4., q.y * 30.)));
  float r = (uSoak * (.34 + .16 * (fbm(q * 2.6 + 1.3) - .5) + .07 * fbm(q * 7. + 2.)) + .03 * fing * uSoak) * (1. - uDry * (.8 + .4 * fbm(q * 5. + 4.)));
  edge = exp(-pow((d - r) / .012, 2.)) * step(.06, uSoak);
  sd = d - max(r, .02);
  return smoothstep(r + .008, r - .022, d) * step(.001, uSoak);
}
vec4 cubicW(float v){ vec4 n = vec4(1., 2., 3., 4.) - v; vec4 s = n * n * n; float x = s.x, y = s.y - 4. * s.x, z = s.z - 4. * s.y + 6. * s.x; return vec4(x, y, z, 6. - x - y - z) / 6.; }
vec4 bspline(sampler2D t, vec2 uv){   // cubic B-spline from 4 bilinear taps: smooths the 8-bit field so isolines stay smooth
  vec2 ts = vec2(textureSize(t, 0)), inv = 1. / ts; uv = uv * ts - .5; vec2 f = fract(uv); uv -= f;
  vec4 xc = cubicW(f.x), yc = cubicW(f.y); vec4 c = uv.xxyy + vec2(-.5, 1.5).xyxy;
  vec4 sm = vec4(xc.xz + xc.yw, yc.xz + yc.yw); vec4 off = (c + vec4(xc.yw, yc.yw) / sm) * inv.xxyy;
  vec4 s0 = texture(t, off.xz), s1 = texture(t, off.yz), s2 = texture(t, off.xw), s3 = texture(t, off.yw);
  float sx = sm.x / (sm.x + sm.y), sy = sm.z / (sm.z + sm.w);
  return mix(mix(s3, s2, sx), mix(s1, s0, sx), sy);
}
// real 2005 infrared frame (NEC camera, knee, 20 min with the soaked bandage; scalar 0 = coldest .. 1 = warmest of that frame)
vec3 irView(vec2 px){
  vec2 sp = (px - uWS * R) / R.y;                       // stage-height units from the wet spot
  float zh = uIRZ;                                        // displayed IR height in stage heights
  vec2 uv = uIRC + vec2(sp.x / (uIRA * zh), -sp.y / zh) + uIRD;
  vec4 tx = bspline(IR, uv); float s = tx.r;
  float body = smoothstep(.2, .8, tx.g);                      // limb mask (room = cold areas connected to the frame edge)
  float c = smoothstep(.34, .07, s) * body;                    // cooler skin = where the soaked bandage was
  vec3 room = lin(vec3(.955, .962, .965)), warm = lin(vec3(.905, .885, .855)), mid = lin(vec3(.83, .92, .945)), core = lin(vec3(.34, .77, .88));
  vec3 skin = c < .5 ? mix(warm, mid, c / .5) : mix(mid, core, (c - .5) / .5);
  vec3 col = mix(room, skin, body);
  float k = s * 16.; float w = max(fwidth(k), .02);
  float ln = 1. - smoothstep(.0, w * 1.15, abs(fract(k - .5) - .5));
  float k2 = s * 4.; float w2 = max(fwidth(k2), .006);
  float mj = 1. - smoothstep(.0, w2 * 1.5, abs(fract(k2 - .5) - .5));
  float edge = 1. - smoothstep(.0, max(fwidth(tx.g), .002) * 1.3, abs(tx.g - .5));   // limb outline
  vec3 ink = mix(lin(vec3(.043, .047, .059)), lin(vec3(.0, .3, .38)), smoothstep(.3, .8, c));
  col = mix(col, ink, clamp((ln * .07 + mj * .22) * body + edge * .85, 0., 1.));
  vec2 ed = min(uv, 1. - uv);                                   // outside the archive frame: plain room tone, soft edge
  float inside = smoothstep(0., .035, min(ed.x, ed.y));
  return mix(room, col, inside);
}
vec3 scene(vec2 px, out float dep, out float wout){
  vec2 n = (px / R) * 2. - 1.;
  vec3 f = normalize(T - C), r = normalize(cross(vec3(0, 1, 0), f)), u = cross(f, r);
  float th = tan(F * .5);
  vec3 dir = normalize(f + n.x * th * (R.x / R.y) * r + n.y * th * u);
  dep = 50.;
  float t = dir.y < -.001 ? -C.y / dir.y : 60.; vec3 P = C + t * dir; dep = t;
  vec2 p = P.xz;
  vec2 uv = p * S;
  vec2 du = dFdx(uv), dv = dFdy(uv);
  float lod = log2(max(length(du), length(dv)) * TX);
  float focus = length(T - C);
  lod = max(lod, 0.) + clamp(abs(1. / t - 1. / focus) * 7., 0., 5.);
  float e = exp2(max(lod, 0.)) / TX;
  vec4 w0 = textureLod(W, uv, lod);
  float hL = textureLod(W, uv - vec2(e, 0), lod).r, hR = textureLod(W, uv + vec2(e, 0), lod).r;
  float hD = textureLod(W, uv - vec2(0, e), lod).r, hU = textureLod(W, uv + vec2(0, e), lod).r;
  float bump = 1.9 / (1. + lod * .6);
  vec3 nrm = normalize(vec3((hL - hR) * bump, 2. * e * S * 1.9, (hD - hU) * bump));
  float edge, sdv; float wt = wet(p, edge, sdv); wout = mix(9., sdv, step(dir.y, -.001));
  vec3 bead = vec3(0); float bm = 0., bsh = 0.;
  for (int k = 0; k < 6; k++) {
    float fk = float(k);
    vec2 bc = uC + vec2(sin(fk * 2.4 + 1.) * .11, cos(fk * 1.7 + .5) * .08) * (.35 + fk * .2);
    float br = (.085 - fk * .008) * (1. - smoothstep(.06 + fk * .02, .4 + fk * .03, uSoak)) * step(.002, uSoak);
    float bd = length(p - bc) / max(br, 1e-4);
    bsh = max(bsh, (1. - smoothstep(.85, 1.25, length(p - bc - vec2(.18, -.28) * br) / max(br, 1e-4))) * step(.002, br));
    if (br > .002 && bd < 1.) { float hz = sqrt(1. - bd * bd); vec3 bn = normalize(vec3((p.x - bc.x) / br, hz * 1.4, (p.y - bc.y) / br));
      bm = max(bm, smoothstep(1., .9, bd)); bead = bn; }
  }
  vec3 L = normalize(vec3(-.35, .55, .8)), L2 = normalize(vec3(.55, .75, -.4)), V = normalize(C - P);
  float hgt = w0.r, gap = w0.b;
  vec3 fab = lin(vec3(.975, .972, .962)) * (.94 + .06 * w0.g);
  vec3 skin = lin(vec3(.86, .74, .66)) * .78;
  vec3 alb = mix(fab, skin, gap * .38);
  vec3 wcol = mix(alb * .84, lin(vec3(.8, .8, .8)), gap * .5);
  alb = mix(alb, wcol, wt);
  vec3 nw = normalize(mix(nrm, vec3(0, 1, 0), .5 * wt));
  if (bm > 0.) { nw = normalize(mix(nw, bead, bm)); }
  float ao = mix(.66, 1., smoothstep(.0, .5, hgt));
  float dif = max(dot(nrm, L), 0.), dif2 = max(dot(nrm, L2), 0.);
  vec3 H = normalize(L + V), H2 = normalize(L2 + V);
  float sp = (pow(max(dot(nw, H), 0.), 140.) * 1.3 + pow(max(dot(nw, H2), 0.), 90.) * .6 + pow(max(dot(nw, H), 0.), 18.) * .1) * (wt * .95 + bm * 1.5)
    + pow(max(dot(nrm, H), 0.), 10.) * .025;
  float fr = pow(1. - max(dot(nw, V), 0.), 5.) * (wt * .18 + bm * .5);
  vec3 col = alb * (.58 * ao + .5 * dif * mix(.75, 1., ao) + .3 * dif2) + vec3(sp) * lin(vec3(.97, .99, 1.)) + vec3(fr) * lin(vec3(.88, .95, 1.));
  col = mix(col, col * .88, edge * .5);
  col *= 1. - .22 * bsh * (1. - bm);
  if (bm > 0.) { float rim = smoothstep(.5, 1., length(bead.xz)); float caus = smoothstep(.45, .0, length(bead.xz - vec2(.25, .3)));
    col = mix(col, alb * mix(1.02, .58, rim) + vec3(sp) * 1.2 + vec3(caus) * .35, bm * .85); }
  float haze = smoothstep(1.9, 5.2, t);
  return mix(col, BG, max(haze * .9, step(-.001, dir.y)));
}
void main(){
  vec2 px = gl_FragCoord.xy;
  float lo = length(px - uLoupe.xy);
  float inL = step(lo, uLoupe.z) * step(.02, uLoupe.w);
  vec2 q = mix(px, uLoupe.xy + (px - uLoupe.xy) / mix(1., 2.4, uLoupe.w), inL);   // macro loupe: magnified coordinates
  float dep, sdq; vec3 col = scene(q, dep, sdq);
  float fw = max(fwidth(sdq), 1e-4);
  float rev = (1. - smoothstep(uRev - fw * 1.5, uRev + fw * .5, sdq)) * smoothstep(0., .14, uScan);   // grows from the wet front
  float tm = mix(max(uMode, rev), 0., uHide);
  if (tm > .001 && uIRon > .5) col = mix(col, irView(q), tm);
  float front = (1. - smoothstep(fw * .6, fw * 1.8, abs(sdq - uRev))) * smoothstep(.0, .03, uRev) * (1. - uHide) * (1. - uMode) * step(sdq, 8.);
  col = mix(col, lin(vec3(0., .706, .847)), front * .95);
  float ring = (1. - smoothstep(0., 1.1, abs(lo - uLoupe.z))) * uLoupe.w;
  vec2 dl = px - uLoupe.xy; float tick = step(abs(dl.x), .7) + step(abs(dl.y), .7);
  tick *= step(uLoupe.z - 9., lo) * step(lo, uLoupe.z) * uLoupe.w;
  col = mix(col, lin(vec3(.043, .047, .059)), clamp(ring * .7 + tick * .7, 0., 1.));
  col = mix(col, col * .97, inL * smoothstep(uLoupe.z - 14., uLoupe.z, lo));
  o = vec4(pow(col, vec3(1. / 2.2)), 1.);
}`;

  /* ---- evaporating molecules (points) ---- */
  const PVS = `#version 300 es
in vec4 s; uniform mat4 M; uniform float uTime, uEvap, uSoak, uDpr, uFoc, uWR; uniform vec2 uC; uniform vec3 C;
out float va; out float vs;
void main(){
  float life = 2.2 + 2.6 * s.z;
  float ph = fract(uTime / life + s.w);
  float ang = s.x * 6.2831853, rr = uWR * (.62 + .45 * s.y);
  vec3 p = vec3(uC.x + cos(ang) * rr, 0., uC.y + sin(ang) * rr * .85);
  float rise = ph * (.05 + .12 * s.z);
  p.y = .006 + rise;
  p.x += sin(uTime * .6 + s.x * 40.) * .025 * ph + ph * .03;
  p.z += cos(uTime * .5 + s.y * 30.) * .02 * ph;
  vec4 cp = M * vec4(p, 1.);
  gl_Position = cp;
  float dep = length(p - C);
  float blur = clamp(abs(1. / dep - 1. / uFoc) * 34., 0., 18.);
  float base = (3. + 4. * fract(s.x * 13.7)) * max(uDpr, 1.2);
  gl_PointSize = base + blur * max(uDpr, 1.2);
  vs = base / gl_PointSize;
  float a = smoothstep(0., .08, ph) * (1. - smoothstep(.3, .55, ph)) * uEvap;
  va = a * (.45 + .55 * vs * vs);
}`;
  const PFS = `#version 300 es
precision mediump float; in float va; in float vs; out vec4 o; uniform float uScan, uMode; uniform vec2 uRes; uniform vec4 uLoupe;
void main(){ vec2 q = gl_PointCoord * 2. - 1.; float d = length(q);
  float a = va * (1. - smoothstep(mix(.5, .9, vs), 1., d)) * (1. - uScan) * (1. - uMode);
  float rim = smoothstep(.55, .85, d) * (1. - smoothstep(.85, 1., d));
  vec3 c = mix(vec3(.97, .99, 1.), vec3(.55, .66, .7), rim * .7);
  o = vec4(c * a, a); }`;

  // tiny mat4 helpers (column-major)
  const persp = (fy, as, n, f) => { const t = 1 / Math.tan(fy / 2), nf = 1 / (n - f); return [t / as, 0, 0, 0, 0, t, 0, 0, 0, 0, (f + n) * nf, -1, 0, 0, 2 * f * n * nf, 0]; };
  const look = (e, c) => {
    let z = [e[0] - c[0], e[1] - c[1], e[2] - c[2]]; let l = Math.hypot(...z); z = z.map(x => x / l);
    let x = [1 * z[2] - 0 * z[1], 0 * z[0] - 0 * z[2], 0 * z[1] - 1 * z[0]]; // up × z
    l = Math.hypot(...x); x = x.map(q => q / l);
    const y = [z[1] * x[2] - z[2] * x[1], z[2] * x[0] - z[0] * x[2], z[0] * x[1] - z[1] * x[0]];
    return [x[0], y[0], z[0], 0, x[1], y[1], z[1], 0, x[2], y[2], z[2], 0,
      -(x[0] * e[0] + x[1] * e[1] + x[2] * e[2]), -(y[0] * e[0] + y[1] * e[1] + y[2] * e[2]), -(z[0] * e[0] + z[1] * e[1] + z[2] * e[2]), 1];
  };
  const mul = (a, b) => { const o = new Array(16).fill(0); for (let c = 0; c < 4; c++) for (let r = 0; r < 4; r++) for (let k = 0; k < 4; k++) o[c * 4 + r] += a[k * 4 + r] * b[c * 4 + k]; return o; };

  function sh(gl, type, src) { const s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s)); return s; }
  function prog(gl, vs, fs) { const p = gl.createProgram(); gl.attachShader(p, sh(gl, gl.VERTEX_SHADER, vs)); gl.attachShader(p, sh(gl, gl.FRAGMENT_SHADER, fs));
    gl.linkProgram(p); if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(p)); return p; }

  function setup(root) {
    const stage = root.querySelector('[data-m-ev-stage]');
    const cv = root.querySelector('canvas');
    const track = root.querySelector('[data-m-ev-track]') || root;
    const readN = root.querySelector('[data-m-ev-n]');
    const toggle = root.querySelector('[data-m-ev-toggle]');
    const dec = root.dataset.dec || ',';
    const live = root.classList.contains('is-live');
    const state = { p: live ? 0 : .97, pt: live ? 0 : .97, mode: 0, modeT: 0, hide: 0, hideT: 0, loupe: [0, 0, 0, 0], loupeT: [0, 0, 0, 0], step: 0, visible: false, drawn: false };
    root.mEv = state;
    let gl = null, P = null, PP = null, U = {}, PU = {}, buf, pbuf, tex, irTex, irA = 1.33, irOn = 0, NP = 0, raf = 0, t0 = performance.now(), scale = 1, slow = 0, fails = 0;

    function progress() {
      if (!live) return 1;
      const r = track.getBoundingClientRect(), vh = innerHeight;
      const st = root.querySelector('[data-m-ev-sticky]');
      const sh_ = st ? st.offsetHeight : vh;
      const run = r.height - sh_;
      return run > 0 ? clamp((-r.top + (st ? parseFloat(getComputedStyle(st).top) || 0 : 0)) / run) : 1;
    }
    function phases(p) {
      return { soak: .05 + .95 * eio(seg(p, .02, .26)), evap: eio(seg(p, .22, .52)), scan: seg(p, .54, .76), dry: eio(seg(p, .28, .54)) * .45 };
    }
    function steps(p) {
      const s = p < .24 ? 1 : p < .53 ? 2 : p < .79 ? 3 : 4;
      if (s !== state.step) { if (state.step) { state.mode = 0; state.hide = 0; root.dataset.mode = ''; } state.step = s; root.dataset.step = s; }
      const view = (state.mode || (!state.hide && phases(p).scan > .02)) ? 'ir' : 'sim'; if (root.dataset.view !== view) root.dataset.view = view;
      state.syncToggle && state.syncToggle();
      const ph = phases(p);
      root.style.setProperty('--m-ev-p1', ph.soak.toFixed(3));
      root.style.setProperty('--m-ev-p2', ph.evap.toFixed(3));
      root.style.setProperty('--m-ev-p3', ease(ph.scan).toFixed(3));
      root.style.setProperty('--m-ev-p4', ease(seg(p, .79, .95)).toFixed(3));
    }

    function initGL() {
      try {
        gl = cv.getContext('webgl2', { antialias: false, alpha: false, premultipliedAlpha: true, preserveDrawingBuffer: !!root.dataset.capture, powerPreference: 'high-performance' });
        if (!gl) return false;
        const bake = prog(gl, VS, BAKE); P = prog(gl, VS, FS); PP = prog(gl, PVS, PFS);
        buf = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, buf); gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
        const TS = innerWidth < 700 ? 1024 : 2048;
        tex = gl.createTexture(); gl.bindTexture(gl.TEXTURE_2D, tex);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, TS, TS, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
        const fb = gl.createFramebuffer(); gl.bindFramebuffer(gl.FRAMEBUFFER, fb); gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
        gl.viewport(0, 0, TS, TS); gl.useProgram(bake); const la = gl.getAttribLocation(bake, 'a'); gl.enableVertexAttribArray(la); gl.vertexAttribPointer(la, 2, gl.FLOAT, false, 0, 0);
        gl.drawArrays(gl.TRIANGLES, 0, 3); gl.bindFramebuffer(gl.FRAMEBUFFER, null); gl.deleteFramebuffer(fb);
        gl.generateMipmap(gl.TEXTURE_2D);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);
        ['W', 'R', 'C', 'T', 'F', 'S', 'TX', 'uSoak', 'uEvap', 'uScan', 'uMode', 'uTime', 'uDry', 'uLoupe', 'uC', 'IR', 'uIRA', 'uIRZ', 'uIRon', 'uIRD', 'uRev', 'uHide', 'uWS', 'uIRC'].forEach(k => U[k] = gl.getUniformLocation(P, k));
        irTex = gl.createTexture(); gl.bindTexture(gl.TEXTURE_2D, irTex);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([200, 200, 200, 255]));
        const irImg = new Image(); irImg.decoding = 'async';
        try { if (new URL(root.dataset.ir, location.href).origin !== location.origin) irImg.crossOrigin = 'anonymous'; } catch (e) {}
        irImg.onload = () => { if (!gl) return; gl.bindTexture(gl.TEXTURE_2D, irTex); gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, irImg);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
          irA = irImg.naturalWidth / irImg.naturalHeight; irOn = 1; kick(); };
        const useIR = () => { if (window.LI_M_IR && window.LI_M_IR.knee) irImg.src = window.LI_M_IR.knee; };
        if (root.dataset.irJs) {   // data texture ships as a JS data URI (theme image assets are re-encoded lossy by the CDN)
          if (window.LI_M_IR && window.LI_M_IR.knee) useIR();
          else { document.addEventListener('li-m-ir', useIR, { once: true });
            if (!document.querySelector('script[data-m-ir]')) { const sc = document.createElement('script'); sc.src = root.dataset.irJs; sc.async = true; sc.dataset.mIr = '1'; document.head.appendChild(sc); } }
        } else if (root.dataset.ir) irImg.src = root.dataset.ir;
        U.TS = TS;
        ['M', 'uTime', 'uEvap', 'uSoak', 'uDpr', 'uFoc', 'uC', 'C', 'uScan', 'uMode', 'uRes', 'uLoupe', 'uWR'].forEach(k => PU[k] = gl.getUniformLocation(PP, k));
        NP = innerWidth < 700 ? 150 : 280;
        const seeds = new Float32Array(NP * 4); for (let i = 0; i < seeds.length; i++) seeds[i] = Math.random();
        pbuf = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, pbuf); gl.bufferData(gl.ARRAY_BUFFER, seeds, gl.STATIC_DRAW);
        cv.addEventListener('webglcontextlost', e => { e.preventDefault(); gl = null; root.classList.remove('is-gl'); });
        return true;
      } catch (e) { if (window.console) console.warn('li-m-evap', e.message); gl = null; return false; }
    }

    function size() {
      const dpr = Math.min(devicePixelRatio || 1, innerWidth < 700 ? 1.75 : 1.5) * scale;
      const w = Math.max(1, Math.round(cv.clientWidth * dpr)), h = Math.max(1, Math.round(cv.clientHeight * dpr));
      if (cv.width !== w || cv.height !== h) { cv.width = w; cv.height = h; }
      return dpr;
    }

    function cam(p, time) {
      const drift = RM.matches ? 0 : Math.sin(time * .00021) * .02;
      const k = ease(p);
      const port = cv.clientHeight > cv.clientWidth * 1.05;
      const C = port ? [ -.1 + .06 * k + drift, .8 - .06 * k, -.5 + .08 * k ] : [ -.12 + .07 * k + drift, .5 - .05 * k, -.86 + .1 * k ];
      const T = [ .02 + .02 * k, 0, .3 ];
      return { C, T };
    }

    function draw(now) {
      if (!gl) return;
      const time = now - t0;
      const dpr = size();
      const W = cv.width, H = cv.height;
      const ph = phases(state.p);
      const { C, T } = cam(state.p, time);
      const fov = (cv.clientWidth < cv.clientHeight ? 46 : 34) * Math.PI / 180;
      const wc = [.03, .3];
      gl.viewport(0, 0, W, H);
      gl.disable(gl.BLEND);
      gl.useProgram(P);
      gl.bindBuffer(gl.ARRAY_BUFFER, buf); const la = gl.getAttribLocation(P, 'a'); gl.enableVertexAttribArray(la); gl.vertexAttribPointer(la, 2, gl.FLOAT, false, 0, 0);
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, tex); gl.uniform1i(U.W, 0);
      gl.uniform2f(U.R, W, H); gl.uniform3fv(U.C, C); gl.uniform3fv(U.T, T); gl.uniform1f(U.F, fov); gl.uniform1f(U.S, 3.2); gl.uniform1f(U.TX, U.TS);
      gl.uniform1f(U.uSoak, ph.soak); gl.uniform1f(U.uEvap, ph.evap); gl.uniform1f(U.uDry, ph.dry);
      const scan = ph.scan;
      const rev = scan > 0 ? -.015 + eio(scan) * .32 : -1;
      gl.uniform1f(U.uScan, scan); gl.uniform1f(U.uRev, rev);
      gl.uniform1f(U.uMode, state.modeT); gl.uniform1f(U.uHide, state.hideT); gl.uniform1f(U.uTime, time * .001);
      const Mvp = mul(persp(fov, W / H, .05, 20), look(C, T));
      const wc4 = [Mvp[0] * wc[0] + Mvp[8] * wc[1] + Mvp[12], Mvp[1] * wc[0] + Mvp[9] * wc[1] + Mvp[13], 0, Mvp[3] * wc[0] + Mvp[11] * wc[1] + Mvp[15]];
      gl.uniform2f(U.uWS, (wc4[0] / wc4[3]) * .5 + .5, (wc4[1] / wc4[3]) * .5 + .5);
      gl.uniform2f(U.uIRC, .45, .5);
      const lp = state.loupe; gl.uniform4f(U.uLoupe, lp[0] * dpr, H - lp[1] * dpr, lp[2] * dpr, lp[3]);
      gl.uniform2fv(U.uC, wc);
      gl.activeTexture(gl.TEXTURE1); gl.bindTexture(gl.TEXTURE_2D, irTex); gl.uniform1i(U.IR, 1);
      const kb = RM.matches ? 0 : time * .00001;
      gl.uniform1f(U.uIRA, irA); gl.uniform1f(U.uIRZ, Math.max(.8, (W / H) / irA * 1.14) - .03 * ease(seg(state.p, .54, 1)) + Math.sin(kb * 3) * .006); gl.uniform1f(U.uIRon, irOn);
      gl.uniform2f(U.uIRD, Math.sin(kb * 2.1) * .008, Math.cos(kb * 1.7) * .006);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      gl.activeTexture(gl.TEXTURE0);
      // molecules
      const ev = ph.evap;
      if (ev > .01) {
        gl.enable(gl.BLEND); gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
        gl.useProgram(PP);
        const M = Mvp;
        gl.uniformMatrix4fv(PU.M, false, new Float32Array(M));
        gl.uniform1f(PU.uTime, time * .001); gl.uniform1f(PU.uEvap, ev); gl.uniform1f(PU.uSoak, ph.soak); gl.uniform1f(PU.uDpr, dpr);
        gl.uniform1f(PU.uFoc, Math.hypot(T[0] - C[0], T[1] - C[1], T[2] - C[2])); gl.uniform2fv(PU.uC, wc); gl.uniform3fv(PU.C, C);
        gl.uniform1f(PU.uScan, Math.min(1, scan * 2.5)); gl.uniform1f(PU.uMode, Math.max(state.modeT, 0)); gl.uniform1f(PU.uWR, .34 * ph.soak * (1 - ph.dry)); gl.uniform2f(PU.uRes, W, H); gl.uniform4f(PU.uLoupe, lp[0] * dpr, H - lp[1] * dpr, lp[2] * dpr, lp[3]);
        gl.bindBuffer(gl.ARRAY_BUFFER, pbuf); const sa = gl.getAttribLocation(PP, 's'); gl.enableVertexAttribArray(sa); gl.vertexAttribPointer(sa, 4, gl.FLOAT, false, 0, 0);
        gl.drawArrays(gl.POINTS, 0, NP);
        gl.disableVertexAttribArray(sa);
      }
      if (!state.drawn) { state.drawn = true; root.classList.add('is-gl'); }
    }

    let last = 0, lastT = 0;
    function frame(now) {
      raf = 0;
      if (!state.visible) return;
      const dt = Math.min(64, now - (lastT || now)); lastT = now;
      if (!RM.matches && !state.lock) state.pt = progress();
      const k = 1 - Math.exp(-dt / 140);
      state.p += (state.pt - state.p) * k; if (Math.abs(state.pt - state.p) < .0005) state.p = state.pt;
      state.modeT += (state.mode - state.modeT) * (1 - Math.exp(-dt / 120)); state.hideT += (state.hide - state.hideT) * (1 - Math.exp(-dt / 120));
      for (let i = 0; i < 4; i++) state.loupe[i] += (state.loupeT[i] - state.loupe[i]) * (1 - Math.exp(-dt / (i === 3 ? 90 : 60)));
      steps(state.p);
      const t1 = performance.now(); draw(now); const cost = performance.now() - t1;
      if (gl && dt > 0 && !state.lock) { slow = slow * .9 + (dt > 30 ? 1 : 0) * .1; if (slow > .6 && scale > .8) { scale -= .1; slow = 0; } }
      const busy = !RM.matches && (Math.abs(state.pt - state.p) > .0005 || Math.abs(state.mode - state.modeT) > .002 || Math.abs(state.hide - state.hideT) > .002 || state.loupe[3] > .002 || phases(state.p).evap > .01);
      if (busy || cost < 0) raf = requestAnimationFrame(frame);
      last = now;
    }
    const kick = () => { if (!raf && state.visible) raf = requestAnimationFrame(frame); };
    root.mEvKick = kick; state.resetScale = () => { scale = 1; };

    // inputs: scroll, toggle, loupe
    addEventListener('scroll', kick, { passive: true });
    addEventListener('resize', kick);
    const irShown = () => state.mode ? true : state.hide ? false : phases(state.p).scan > .02;
    toggle && toggle.addEventListener('click', () => { if (irShown()) { state.mode = 0; state.hide = 1; } else { state.mode = 1; state.hide = 0; }
      root.dataset.mode = state.mode ? 'ir' : ''; kick(); });
    state.syncToggle = () => { const on = irShown(); if (toggle && toggle.getAttribute('aria-pressed') !== String(on)) toggle.setAttribute('aria-pressed', String(on)); };
    let hideT;
    const pt = (e, on) => { const r = stage.getBoundingClientRect(); const rad = Math.max(54, Math.min(110, r.width * .13));
      state.loupeT = [e.clientX - r.left, e.clientY - r.top, rad, on ? 1 : 0];
      if (state.loupe[3] < .01) { state.loupe[0] = state.loupeT[0]; state.loupe[1] = state.loupeT[1]; state.loupe[2] = rad; }
      root.classList.add('is-touched'); kick(); };
    stage.addEventListener('pointermove', e => { if (e.pointerType === 'mouse') pt(e, true); });
    stage.addEventListener('pointerleave', e => { if (e.pointerType === 'mouse') { state.loupeT[3] = 0; kick(); } });
    stage.addEventListener('pointerdown', e => { if (e.pointerType !== 'mouse' && !e.target.closest('button')) { pt(e, true); clearTimeout(hideT); hideT = setTimeout(() => { state.loupeT[3] = 0; kick(); }, 1800); } });
    stage.addEventListener('pointerup', e => { if (e.pointerType !== 'mouse') { clearTimeout(hideT); hideT = setTimeout(() => { state.loupeT[3] = 0; kick(); }, 1400); } });

    const io = new IntersectionObserver(es => es.forEach(en => {
      state.visible = en.isIntersecting;
      if (en.isIntersecting && !gl && !state.tried) { state.tried = true; if (initGL()) { kick(); } }
      kick();
    }), { rootMargin: '200px 0px' });
    io.observe(stage);
    RM.addEventListener && RM.addEventListener('change', kick);
    steps(state.p);
    // test hook (screenshots/frames): window.LI_M_EV(index, p, mode, loupe)
    window.LI_M_EV = window.LI_M_EV || ((i, p, mode, lp) => { const r = roots[i || 0].mEv; if (!r) return; r.lock = true; r.resetScale && r.resetScale(); if (p != null) { r.p = r.pt = p; } if (mode != null) { r.mode = r.modeT = mode; }
      if (lp) { r.loupe = lp.slice(); r.loupeT = lp.slice(); } roots[i || 0].mEvKick(); });
  }

  const start = () => roots.forEach(setup);
  if ('requestIdleCallback' in window) requestIdleCallback(start, { timeout: 1500 }); else setTimeout(start, 200);
})();
